import React, { useMemo, useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';
import { AcademyMyPageTabItems, AcademyMyPageLogoutButton } from "./info";
import { Button } from 'components/atoms/index';
import * as styles from 'components/organisms/Table/Table.css'
import Pagination from 'components/atoms/Pagination/Pagination';
import { Icon } from '@/icons';
import { Input } from 'components/atoms/Input/Input';
import { Dialog, Modal } from '@/components/organisms/Modal/Dialog';
import { academyClassApi } from '@/services/api';
import { useSessionSlug } from '@/hooks/useSessionSlug';
import { getSlugPrefix } from '@/utils/slug';

interface ClassRow {
    id: number
    name: string
    studentCount: number
    registrant: string
    registrationDate: string
}

// 서버 데이터 상태
const useClassList = (academyCode: string, page: number, size: number) => {
    const [rows, setRows] = useState<ClassRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const fetchList = async (p = page, s = size) => {
        if (!academyCode) { setRows([]); setTotal(0); return; }
        setLoading(true);
        try {
            const res: any = await academyClassApi.list({ academyCode, page: p - 1, size: s });
            const data = res?.data || res;
            const content = data?.content || [];
            const sorted = [...content].sort((a: any, b: any) => {
                const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
                const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
                return bTime - aTime;
            }); // [QA Fix] 최신 등록순으로 정렬
            const mapped: ClassRow[] = sorted.map((it: any) => ({
                id: it.id,
                name: it.name,
                studentCount: it.studentCount ?? 0,
                registrant: it.registrant || '-',
                registrationDate: (it.createdAt || '').toString().split('T')[0] || '-',
            }));
            setRows(mapped);
            setTotal(data?.totalElements ?? mapped.length);
        } finally { setLoading(false); }
    };
    return { rows, total, loading, fetchList, setRows };
};

// 컬럼 렌더링용 메타 정보
const columns = [
    { key: 'id' as const, render: (row: ClassRow) => row.id },
    { key: 'name' as const, render: (row: ClassRow) => row.name },
    { key: 'studentCount' as const, render: (row: ClassRow) => (
        <div className="flex gap-2 items-center lg:block">
            <span className="text-xs text-gray-400 lg:hidden">소속 학생 수</span>
            <span>{Number.isFinite(row.studentCount as any) ? row.studentCount : 0}명</span>
        </div>
    ) },
    { key: 'registrant' as const, render: (row: ClassRow) => (
        <div className="flex gap-2 items-center lg:block">
            <span className="text-xs text-gray-400 lg:hidden">등록자</span>
            <span>{row.registrant}</span>
        </div>
    ) },
    { key: 'registrationDate' as const, render: (row: ClassRow) => (
        <div className="flex gap-2 items-center lg:block">
            <span className="text-xs text-gray-400 lg:hidden">등록일</span>
            <span>{row.registrationDate}</span>
        </div>
    ) },
]



export default function AcademyMypageClass() {
    const [activeTab, setActiveTab] = useState<'info' | 'plan' | 'paper' | 'payment' | 'class'>('class');
    const navigate = useNavigate();
    const location = useLocation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { slug: sessionSlug, academyCode: sessionAcademyCode, status: sessionStatus, role: sessionRole } = useSessionSlug();
    const slugPrefix = sessionSlug ? `/${sessionSlug}` : getSlugPrefix(location.pathname);
    const storedRole = typeof window !== 'undefined' ? (localStorage.getItem('academy_role') || '') : '';
    const isTeacher = (sessionRole || storedRole || '').toUpperCase() === 'TEACHER';
    useEffect(() => {
        if (isTeacher) {
            navigate(`${slugPrefix}/academy/mypage/info`, { replace: true });
        }
    }, [isTeacher, slugPrefix, navigate]);
    const academyCode = sessionAcademyCode || '';
    const [page, setPage] = useState(1);
    const pageSize = 10;
    const { rows, total, fetchList } = useClassList(academyCode, page, pageSize);
    const [searchText, setSearchText] = useState('');
    const createSubmitRef = useRef<() => void>(() => {});

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (sessionStatus === 'ready' && academyCode) {
            fetchList(1, pageSize);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [academyCode, pageSize, sessionStatus]);

    const handleRowClick = (classItem: ClassRow) => {
        setEditTarget(classItem);
        setIsEditDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
    };

    const handleOpenDialog = () => {
        setIsDialogOpen(true);
    };

    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<ClassRow | null>(null);

    const filteredRows = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r =>
            (r.name || '').toLowerCase().includes(q) ||
            String(r.id).includes(q) ||
            (r.registrant || '').toLowerCase().includes(q)
        );
    }, [rows, searchText]);

    if (isTeacher) {
        return null;
    }

    return (
        <main className='flex flex-col flex-1'>
            <div className='flex flex-row justify-between items-center pt-8'>
                <h1 className='heading1'>마이페이지</h1>
                <AcademyMyPageLogoutButton />
            </div>
            <div className="flex flex-col flex-1 mt-5">
                <AcademyMyPageTabItems activeTab={activeTab} setActiveTab={setActiveTab} />
                <div className='flex flex-col flex-1'>
                    <section className='flex gap-2 justify-between items-start my-5 w-full'>
                    <Input
                        placeholder='검색어를 입력해주세요.'
                        className='max-w-[500px]'
                        icon={<Icon name="search" size={20}  />}
                        value={searchText}
                        onChange={(e:any)=>setSearchText(e.target.value)}
                        onKeyDown={(e:any)=>{ if(e.key==='Enter'){ e.preventDefault(); setSearchText((v:string)=>v); } }}
                    />
                    <Button onClick={handleOpenDialog}>클래스 등록</Button>
                    </section>
                    
                    <section className='flex flex-col flex-1 justify-between w-full'>
            {/* no_list */}
            {/* <p>리스트가 없습니다.</p> */}
            <div className='w-full'>
                {rows.length === 0 ? (
                    <div className='flex flex-col justify-center items-center py-16 w-full text-gray-500 bg-white rounded-2xl border border-gray-100'>
                        등록된 클래스가 없습니다.
                    </div>
                ) : (
                    <>
                <ul className={`${styles.academyHeaderList} w-full`}>
                    <li className={`${styles.headerItem} max-w-[80px] justify-center`}>순번</li>
                    <li className={`${styles.headerItem}`}>클래스명</li>
                    <li className={`${styles.headerItem} max-w-[200px] lg:flex justify-center`}>소속 학생 수</li>
                    <li className={`${styles.headerItem} max-w-[200px]`}>등록자</li>
                    <li className={`${styles.headerItem} max-w-[200px]`}>등록일</li>
                </ul>
                <div className='flex flex-col gap-4 w-full lg:gap-0'>
                            {filteredRows.map((row, index) => {
                                const descendingNo = total > 0
                                    ? total - ((page - 1) * pageSize + index)
                                    : filteredRows.length - index;
                                return (
                            <ul 
                                key={row.id} 
                                className={`${styles.academyBodyList} lg:shadow-none table-card cursor-pointer hover:bg-gray-50 transition-colors !grid grid-cols-2 lg:!flex`}
                            >
                        {columns.map(({ key, render }) => (
                            <li
                            key={key}
                            onClick={() => handleRowClick(row)}
                            className={`${styles.bodyItem} ${
                                key === 'id' ? 'max-w-[80px] !hidden lg:!flex justify-center' :
                                key === 'name' ? 'col-span-2 lg:col-span-1 ' :
                                key === 'studentCount' ? 'absolute top-5 right-5 lg:static max-w-[200px] lg:flex justify-center' :
                                key === 'registrant' ? 'text-sm text-gray-500 max-w-[200px] lg:static col-span-1' :
                                key === 'registrationDate' ? 'text-sm text-gray-500 max-w-[200px] lg:static col-span-1' :
                                ''
                            }`}
                            >
                            {key === 'id' ? descendingNo : render(row)}
                            </li>
                        ))}
                        </ul>
                    )})}
                </div>
                    </>
                )}
            </div>
                <div className='py-5'>
            <Pagination
                total={total}
                pageSize={pageSize}
                current={page}
                onChange={(p:number) => { setPage(p); fetchList(p, pageSize); }}
            />
            </div>
                    </section>
                </div>
            </div>

            <Dialog
                open={isDialogOpen}
                onCancel={handleCloseDialog}
                onOk={() => { if (createSubmitRef.current) createSubmitRef.current(); }}
                okText="저장하기"
                cancelText="취소"
            >
                <ClassCreateForm 
                    academyCode={academyCode}
                    bindSubmit={(fn: () => void) => { createSubmitRef.current = fn; }}
                    onSaved={() => { setIsDialogOpen(false); fetchList(1, pageSize); }} 
                />
            </Dialog>

            <Dialog
                open={isEditDialogOpen}
                onCancel={() => setIsEditDialogOpen(false)}
                hideFooter
            >
                {editTarget && (
                    <ClassEditForm
                        classRoomId={editTarget.id}
                        initialName={editTarget.name}
                        onCancel={() => setIsEditDialogOpen(false)}
                        onSaved={() => { setIsEditDialogOpen(false); fetchList(1, pageSize); }}
                    />
                )}
            </Dialog>
        </main>
    )
}

function ClassCreateForm({ onSaved, bindSubmit, academyCode }: { onSaved: () => void, bindSubmit: (fn: () => void) => void, academyCode: string }) {
    const [name, setName] = useState('');
    // const [saving, setSaving] = useState(false);
    const handleSave = async () => {
        if (!name.trim() || !academyCode) return;
        // setSaving(true);
        try {
            await academyClassApi.create({ academyCode, name });
            onSaved();
        } catch (e: any) {
            const msg = e?.response?.data?.message || '클래스 생성에 실패했습니다.';
            Modal.alert(msg);
        } finally { /* setSaving(false); */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => { bindSubmit(handleSave); }, [name]);
    return (
        <div className='flex flex-col gap-4 p-5 pb-0'>
            <h2 className='heading1'>클래스 등록</h2>
            <Input placeholder='클래스 명' value={name} onChange={(e:any)=>setName(e.target.value)} />
            {/* 저장 버튼은 Dialog의 OK 버튼 하나만 사용 */}
        </div>
    );
}

function ClassEditForm({ classRoomId, initialName, onSaved, onCancel }: { classRoomId: number, initialName: string, onSaved: () => void, onCancel: () => void }) {
    const [name, setName] = useState(initialName || '');
    const [deleting, setDeleting] = useState(false);
    const handleSave = async () => {
        if (!name.trim()) return;
        try {
            await academyClassApi.update({ id: classRoomId, name: name.trim() });
            onSaved();
        } catch (e: any) {
            const msg = e?.response?.data?.message || '클래스명 수정에 실패했습니다.';
            Modal.alert(msg);
        }
    };
    const handleDelete = async () => {
        const ok = await Modal.confirm(
            <div className='px-5 pt-7 text-center'>
                해당 클래스를 삭제하시겠습니까?
                <br />
                학생이 소속된 클래스는 삭제할 수 없습니다.
            </div>,
            '삭제',
            '취소'
        );
        if (!ok) return;
        setDeleting(true);
        try {
            await academyClassApi.remove(classRoomId);
            onSaved();
        } catch (e: any) {
            const msg = e?.response?.data?.message || '클래스 삭제에 실패했습니다.';
            Modal.alert(msg);
        } finally { setDeleting(false); }
    };
    return (
        <div className='flex flex-col gap-4 p-5 pb-0'>
            <h2 className='heading1'>클래스명 수정</h2>
            <Input placeholder='클래스 명' value={name} onChange={(e:any)=>setName(e.target.value)} />
            <div className='grid grid-cols-3 gap-3 pt-2 pb-3'>
                <Button className='w-full' intent='danger' onClick={handleDelete} disabled={deleting}>삭제</Button>
                <Button className='w-full' intent='secondary' onClick={onCancel}>닫기</Button>
                <Button className='w-full' onClick={handleSave}>수정하기</Button>
            </div>
        </div>
    );
}